import { useState, useEffect } from "react";
import { API_ENDPOINTS, API_URL } from "@/constants/constants";
import {
  RefreshCw,
  Search,
  Edit,
  ToggleLeft,
  ToggleRight,
  X,
  PlusCircle,
  Eye,
  User,
} from "lucide-react";

// Interface pour les professeurs
interface Professor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  fonction?: string;
  service?: string;
  isVerified?: boolean;
  isActive?: boolean;
  createdAt?: string | Date;
  role?: string;
  birthdate?: string;
  profileImage?: string;
}

// Interface pour le formulaire de mise à jour d'un professeur
interface UpdateProfessorForm {
  firstName: string;
  lastName: string;
  email: string;
  fonction: string;
  service: string;
  profileImage: string;
  role: string;
  birthdate?: string;
  isActive?: boolean;
}

// Interface pour le formulaire de création d'un professeur
interface CreateProfessorForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  fonction: string;
  service: string;
  profileImage: string;
  role: string;
  birthdate?: string;
}

const AdminProfessors = () => {
  const [professors, setProfessors] = useState<Professor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusUpdateLoading, setStatusUpdateLoading] = useState<string | null>(
    null
  );

  // États pour le modal d'édition
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedProfessor, setSelectedProfessor] = useState<Professor | null>(
    null
  );
  const [updateForm, setUpdateForm] = useState<UpdateProfessorForm>({
    firstName: "",
    lastName: "",
    email: "",
    fonction: "",
    service: "",
    profileImage: "",
    role: "FORMATEUR", // Rôle fixé à FORMATEUR
    birthdate: "",
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // États pour le modal de création
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateProfessorForm>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    fonction: "",
    service: "",
    profileImage: "",
    role: "FORMATEUR", // Rôle fixé à FORMATEUR
    birthdate: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchProfessors();
  }, []);

  const fetchProfessors = async () => {
    setLoading(true);
    setError("");

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(API_ENDPOINTS.AUTH.GET_TEACHERS, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la récupération des professeurs"
        );
      }

      // Affichage des données brutes pour déboguer
      console.log("Données des professeurs reçues:", data);
      // Affichons la structure du premier professeur si disponible
      if (data && data.length > 0) {
        console.log(
          "Structure du premier professeur:",
          JSON.stringify(data[0], null, 2)
        );
        console.log("Type de isActive:", typeof data[0].isActive);
        console.log("Type de createdAt:", typeof data[0].createdAt);
      }

      setProfessors(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur de récupération des professeurs:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les professeurs en fonction du terme de recherche
  const filteredProfessors = professors.filter(
    (professor) =>
      professor.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      professor.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      professor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (professor.fonction &&
        professor.fonction.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (professor.service &&
        professor.service.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString: string | Date | undefined) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      // Vérification si la date est valide
      if (Number.isNaN(date.getTime())) return "-";

      // Format avec date et heure
      return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(date);
    } catch (error) {
      console.error("Erreur formatage date:", error);
      return "-";
    }
  };

  // Vérifier si l'utilisateur est actif
  const isUserActive = (professor: Professor) => {
    // Vérifier les différentes possibilités selon la structure de l'API
    if (typeof professor.isActive === "boolean") return professor.isActive;

    // Pour les cas où c'est une chaîne ou un nombre
    if (
      professor.isActive === "true" ||
      professor.isActive === 1 ||
      professor.isActive === "1"
    )
      return true;

    // Par défaut (valeur null, undefined ou falsy)
    return false;
  };

  const isVerified = (professor: Professor) => {
    if (typeof professor.isVerified === "boolean") return professor.isVerified;

    // Pour les cas où c'est une chaîne ou un nombre
    if (
      professor.isVerified === "true" ||
      professor.isVerified === 1 ||
      professor.isVerified === "1"
    )
      return true;

    // Par défaut (valeur null, undefined ou falsy)
    return false;
  };

  const handleToggleProfessorStatus = async (
    professorId: string,
    currentStatus: boolean
  ) => {
    setStatusUpdateLoading(professorId);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Utiliser le même modèle d'URL que pour les utilisateurs
      const apiUrl = API_ENDPOINTS.AUTH.LOGIN.replace(
        "/login",
        `/users/${professorId}/status`
      );

      const response = await fetch(apiUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ isActive: !currentStatus }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.message || "Erreur lors de la mise à jour du statut"
        );
      }

      // Rafraîchir la liste des professeurs après la mise à jour
      fetchProfessors();
    } catch (err) {
      console.error("Erreur lors de la modification du statut:", err);
      alert(err instanceof Error ? err.message : "Une erreur est survenue");
    } finally {
      setStatusUpdateLoading(null);
    }
  };

  const handleEditProfessor = (professor: Professor) => {
    setSelectedProfessor(professor);

    // Formatage de la date de naissance au format YYYY-MM-DD requis par l'input type="date"
    let formattedBirthdate = "";
    if (professor.birthdate) {
      try {
        // Essayer de créer une date JavaScript à partir de la valeur
        const date = new Date(professor.birthdate);

        // Vérifier si la date est valide
        if (!Number.isNaN(date.getTime())) {
          // Format YYYY-MM-DD pour l'élément input type="date"
          const year = date.getFullYear();
          // Les mois sont indexés à partir de 0 en JavaScript, donc on ajoute 1
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          formattedBirthdate = `${year}-${month}-${day}`;
        }
      } catch (error) {
        console.error("Erreur de formatage de la date de naissance:", error);
      }
    }

    setUpdateForm({
      firstName: professor.firstName,
      lastName: professor.lastName,
      email: professor.email,
      fonction: professor.fonction || "",
      service: professor.service || "",
      profileImage: professor.profileImage || "",
      role: "FORMATEUR", // Rôle fixé à FORMATEUR
      birthdate: formattedBirthdate,
      isActive: isUserActive(professor),
    });
    setIsUpdateModalOpen(true);

    // Afficher les données dans la console pour débogage
    console.log("Date de naissance originale:", professor.birthdate);
    console.log("Date de naissance formatée:", formattedBirthdate);
  };

  const handleUpdateInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setUpdateForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateProfessor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProfessor) return;

    setUpdateLoading(true);
    setUpdateError("");
    setUpdateSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Préparation des données à envoyer
      const formData = { ...updateForm };

      // L'endpoint pour mettre à jour un utilisateur
      const apiUrl = API_ENDPOINTS.AUTH.LOGIN.replace(
        "/login",
        `/users/${selectedProfessor.id}`
      );

      console.log("Mise à jour professeur:", apiUrl, formData);

      const response = await fetch(apiUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      // Gestion des erreurs
      if (!response.ok) {
        let errorMessage = "Erreur lors de la mise à jour du professeur";
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = `Erreur ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Rafraîchir la liste des professeurs
      fetchProfessors();

      setUpdateSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsUpdateModalOpen(false);
        setUpdateSuccess(false);
        setSelectedProfessor(null);
      }, 2000);
    } catch (err) {
      setUpdateError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur lors de la mise à jour du professeur:", err);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCreateInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateProfessor = async (e: React.FormEvent) => {
    e.preventDefault();

    setCreateLoading(true);
    setCreateError("");
    setCreateSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Préparation des données à envoyer
      const formData = { ...createForm };

      const response = await fetch(API_ENDPOINTS.AUTH.REGISTER, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      // Gestion des erreurs
      if (!response.ok) {
        let errorMessage = "Erreur lors de la création du professeur";
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = `Erreur ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Rafraîchir la liste des professeurs
      fetchProfessors();

      setCreateSuccess(true);

      // Réinitialiser le formulaire
      setCreateForm({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        fonction: "",
        service: "",
        profileImage: "",
        role: "FORMATEUR",
        birthdate: "",
      });

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsCreateModalOpen(false);
        setCreateSuccess(false);
      }, 2000);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur lors de la création du professeur:", err);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">Professeurs</h1>
      <p className="mt-4">Gérez les professeurs de la plateforme.</p>
      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Liste des professeurs</h2>
          <div className="flex gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher..."
                className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search
                className="absolute left-3 top-2.5 text-gray-400"
                size={18}
              />
            </div>
            <button
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
              onClick={fetchProfessors}
              disabled={loading}
            >
              <RefreshCw
                size={18}
                className={`text-gray-600 ${loading ? "animate-spin" : ""}`}
              />
              <span>Actualiser</span>
            </button>
            <button
              className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <PlusCircle size={18} />
              <span>Créer un professeur</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <RefreshCw size={24} className="animate-spin text-blue-500" />
            <span className="ml-2">Chargement des professeurs...</span>
          </div>
        ) : filteredProfessors.length === 0 ? (
          <p className="text-center py-8 text-gray-500">
            {searchTerm
              ? "Aucun professeur ne correspond à votre recherche"
              : "Aucun professeur pour le moment."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Photo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fonction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vérifié
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date de création
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredProfessors.map((professor, index) => (
                  <tr key={professor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-500">
                        {index + 1}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                        {professor.profileImage ? (
                          <img
                            src={`${API_URL}/${professor.profileImage}`}
                            alt={`${professor.firstName} ${professor.lastName}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = "/images/avatar-placeholder.png";
                            }}
                          />
                        ) : (
                          <User size={20} className="text-gray-500" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {professor.firstName} {professor.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {professor.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {professor.fonction || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {professor.service || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          isVerified(professor)
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {isVerified(professor) ? "Oui" : "Non"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          isUserActive(professor)
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {isUserActive(professor) ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(professor.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditProfessor(professor)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                          title="Modifier"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() =>
                            handleToggleProfessorStatus(
                              professor.id,
                              isUserActive(professor)
                            )
                          }
                          className={`p-1 rounded ${
                            isUserActive(professor)
                              ? "text-green-600 hover:text-green-800 hover:bg-green-100"
                              : "text-red-600 hover:text-red-800 hover:bg-red-100"
                          }`}
                          title={
                            isUserActive(professor) ? "Désactiver" : "Activer"
                          }
                          disabled={statusUpdateLoading === professor.id}
                        >
                          {statusUpdateLoading === professor.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                          ) : isUserActive(professor) ? (
                            <ToggleRight size={18} />
                          ) : (
                            <ToggleLeft size={18} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal d'édition de professeur */}
      {isUpdateModalOpen && selectedProfessor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-3xl relative">
            <button
              onClick={() => setIsUpdateModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Modifier le professeur</h2>

            {updateSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Professeur modifié avec succès!</p>
              </div>
            )}

            {updateError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{updateError}</p>
              </div>
            )}

            <form onSubmit={handleUpdateProfessor}>
              <div className="grid grid-cols-2 gap-6">
                {/* Première colonne */}
                <div>
                  <div className="mb-4">
                    <label htmlFor="update-first-name" className="block text-gray-700 text-sm font-bold mb-2">
                      Prénom
                    </label>
                    <input
                      id="update-first-name"
                      type="text"
                      name="firstName"
                      value={updateForm.firstName}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="update-email" className="block text-gray-700 text-sm font-bold mb-2">
                      Email
                    </label>
                    <input
                      id="update-email"
                      type="email"
                      name="email"
                      value={updateForm.email}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="update-fonction" className="block text-gray-700 text-sm font-bold mb-2">
                      Fonction
                    </label>
                    <input
                      id="update-fonction"
                      type="text"
                      name="fonction"
                      value={updateForm.fonction}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Developer"
                      required
                    />
                  </div>
                </div>

                {/* Deuxième colonne */}
                <div>
                  <div className="mb-4">
                    <label htmlFor="update-last-name" className="block text-gray-700 text-sm font-bold mb-2">
                      Nom
                    </label>
                    <input
                      id="update-last-name"
                      type="text"
                      name="lastName"
                      value={updateForm.lastName}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="update-service" className="block text-gray-700 text-sm font-bold mb-2">
                      Service
                    </label>
                    <input
                      id="update-service"
                      type="text"
                      name="service"
                      value={updateForm.service}
                      onChange={handleUpdateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: IT"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="update-role" className="block text-gray-700 text-sm font-bold mb-2">
                      Rôle
                    </label>
                    <input
                      id="update-role"
                      type="text"
                      value="FORMATEUR"
                      className="w-full p-2 border rounded-md bg-gray-100 text-gray-600"
                      disabled
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsUpdateModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
                  disabled={updateLoading}
                >
                  {updateLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Mettre à jour
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de création de professeur */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-3xl relative">
            <button
              onClick={() => setIsCreateModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Créer un professeur</h2>

            {createSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Professeur créé avec succès!</p>
              </div>
            )}

            {createError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{createError}</p>
              </div>
            )}

            <form onSubmit={handleCreateProfessor}>
              <div className="grid grid-cols-2 gap-6">
                {/* Première colonne */}
                <div>
                  <div className="mb-4">
                    <label htmlFor="create-first-name" className="block text-gray-700 text-sm font-bold mb-2">
                      Prénom
                    </label>
                    <input
                      id="create-first-name"
                      type="text"
                      name="firstName"
                      value={createForm.firstName}
                      onChange={handleCreateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-email" className="block text-gray-700 text-sm font-bold mb-2">
                      Email
                    </label>
                    <input
                      id="create-email"
                      type="email"
                      name="email"
                      value={createForm.email}
                      onChange={handleCreateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-fonction" className="block text-gray-700 text-sm font-bold mb-2">
                      Fonction
                    </label>
                    <input
                      id="create-fonction"
                      type="text"
                      name="fonction"
                      value={createForm.fonction}
                      onChange={handleCreateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Developer"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-birthdate" className="block text-gray-700 text-sm font-bold mb-2">
                      Date de naissance
                    </label>
                    <input
                      id="create-birthdate"
                      type="date"
                      name="birthdate"
                      value={createForm.birthdate || ""}
                      onChange={handleCreateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Deuxième colonne */}
                <div>
                  <div className="mb-4">
                    <label htmlFor="create-last-name" className="block text-gray-700 text-sm font-bold mb-2">
                      Nom
                    </label>
                    <input
                      id="create-last-name"
                      type="text"
                      name="lastName"
                      value={createForm.lastName}
                      onChange={handleCreateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-service" className="block text-gray-700 text-sm font-bold mb-2">
                      Service
                    </label>
                    <input
                      id="create-service"
                      type="text"
                      name="service"
                      value={createForm.service}
                      onChange={handleCreateInputChange}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: IT"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-password" className="block text-gray-700 text-sm font-bold mb-2">
                      Mot de passe
                    </label>
                    <div className="relative">
                      <input
                        id="create-password"
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={createForm.password}
                        onChange={handleCreateInputChange}
                        className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <X size={18} aria-label="Masquer le mot de passe" />
                        ) : (
                          <Eye
                            size={18}
                            aria-label="Afficher le mot de passe"
                          />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="create-role" className="block text-gray-700 text-sm font-bold mb-2">
                      Rôle
                    </label>
                    <input
                      type="text"
                      value="FORMATEUR"
                      className="w-full p-2 border rounded-md bg-gray-100 text-gray-600"
                      disabled
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2"
                  disabled={createLoading}
                >
                  {createLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProfessors;
